import { useQuery } from "@tanstack/react-query"

import { api } from "@/api/cliente"
import { Exportar } from "@/componentes/exportar/Exportar"
import type { Columna, DocumentoExportable } from "@/componentes/exportar/modelo"
import { monto } from "@/componentes/tesoreria/dinero"
import { EstadoCarga, EstadoError } from "@/componentes/ui/Estados"
import { Insignia } from "@/componentes/ui/Insignia"
import { Cabecera, Cuerpo, Fila, Tabla, TablaVacia, Td, Th } from "@/componentes/ui/Tabla"
import { comoFecha } from "@/lib/fechas"

/** Lo que manda `GET /resumen-macro/facturacion` (`schemas/resumen_macro.py`). */
export type Facturacion = {
  fecha: string
  grupos: Record<string, string>
  clases: Record<string, string>
  expedientes: {
    expediente: string
    cliente: string
    clase: string
    subestatus: string | null
    moneda: string
    total: string
    pendiente: string
    pendiente_usd: string | null
    por_grupo: Record<string, string>
    renglones: { grupo: string; concepto: string; monto: string; cobrado: string }[]
  }[]
  por_clase: Record<string, string>
  por_grupo: Record<string, string>
  total: string
  errores: string[]
}

const dinero = (v: string | number | undefined) =>
  `$ ${monto(typeof v === "number" ? v.toFixed(2) : (v ?? "0"))}`

/**
 * La proyección de facturación por expediente (RM-6, 23/10; `services/facturacion_semana.py`):
 * lo que se le factura a cada cliente —agenciamiento y comisiones, gastos, habilitación,
 * fletes…— y cuándo se cobra: exigible esta semana, en valija la próxima. El mismo cuadro es
 * el reporte de los viernes (`/m/<token>`).
 */
export function VistaFacturacion({ f }: { f: Facturacion }) {
  const grupos = Object.keys(f.grupos).filter((g) => f.por_grupo[g] !== undefined)
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="tarjeta p-3 text-center">
          <span className="block text-xs uppercase" style={{ color: "var(--fg-muted)" }}>
            Esta semana
          </span>
          <span className="block text-lg font-semibold tabular-nums">
            {dinero(f.por_clase.EXIGIBLE)}
          </span>
          <span className="block text-xs">exigible</span>
        </div>
        <div className="tarjeta p-3 text-center">
          <span className="block text-xs uppercase" style={{ color: "var(--fg-muted)" }}>
            Semana próxima
          </span>
          <span className="block text-lg font-semibold tabular-nums">
            {dinero(f.por_clase.VALIJA)}
          </span>
          <span className="block text-xs">en valija</span>
        </div>
        <div className="tarjeta p-3 text-center">
          <span className="block text-xs uppercase" style={{ color: "var(--fg-muted)" }}>
            Total por cobrar
          </span>
          <span className="block text-lg font-semibold tabular-nums">{dinero(f.total)}</span>
          <span className="block text-xs">{f.expedientes.length} expediente(s)</span>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 text-xs">
        {grupos.map((g) => (
          <span key={g} className="tarjeta px-3 py-1.5 tabular-nums">
            {f.grupos[g]}: {dinero(f.por_grupo[g])}
          </span>
        ))}
      </div>

      {f.errores.length > 0 && (
        <p className="text-xs" style={{ color: "var(--warning)" }}>
          Sin detalle del 2.0: {f.errores.join(" · ")}
        </p>
      )}

      <Tabla etiqueta="Facturación por expediente">
        <Cabecera>
          <Th>Expediente</Th>
          <Th>Cobro</Th>
          {grupos.map((g) => (
            <Th key={g} derecha>
              {f.grupos[g]}
            </Th>
          ))}
          <Th derecha>Pendiente</Th>
        </Cabecera>
        <Cuerpo>
          {f.expedientes.length === 0 && (
            <TablaVacia colSpan={grupos.length + 3}>Nada por cobrar.</TablaVacia>
          )}
          {f.expedientes.map((e) => (
            <Fila key={`${e.expediente}-${e.moneda}`}>
              <Td>
                <span className="tabular-nums">{e.expediente}</span>
                <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
                  {e.cliente}
                </span>
              </Td>
              <Td>
                <Insignia tono={e.clase === "EXIGIBLE" ? "exito" : "info"}>
                  {e.clase === "EXIGIBLE" ? "Esta semana" : "Semana próxima"}
                </Insignia>
                {e.subestatus && (
                  <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
                    {e.subestatus}
                  </span>
                )}
              </Td>
              {grupos.map((g) => (
                <Td key={g} derecha numero>
                  {e.por_grupo[g] ? monto(e.por_grupo[g]) : "—"}
                </Td>
              ))}
              <Td derecha numero>
                {monto(e.pendiente)} {e.moneda}
              </Td>
            </Fila>
          ))}
        </Cuerpo>
      </Tabla>
    </div>
  )
}

/** La pestaña del panel: la facturación de HOY, leída del 2.0 al abrirla. */
export function FacturacionEnVivo() {
  const consulta = useQuery({
    queryKey: ["resumen-macro", "facturacion"],
    queryFn: () => api.get<Facturacion>("/api/v1/resumen-macro/facturacion"),
  })
  if (consulta.isPending) return <EstadoCarga filas={4} />
  if (consulta.isError) return <EstadoError titulo="No se pudo leer" error={consulta.error} />
  const f = consulta.data
  return (
    <div className="space-y-3">
      {f.expedientes.length > 0 && (
        <div className="flex flex-wrap justify-end gap-2">
          <Exportar
            titulo="Facturación por cobrar"
            subtitulo={subtituloDe(f)}
            documento={() => documentoDeFacturacion(f)}
          />
        </div>
      )}
      <VistaFacturacion f={f} />
    </div>
  )
}

const subtituloDe = (f: Facturacion) =>
  `Leída del 2.0 al ${comoFecha(f.fecha)}, por expediente y grupo`

/**
 * La facturación con la moneda de cada expediente en su columna: la pantalla escribe
 * «1.200,00 USD» y «35.000,00 BS» en la misma columna, que leída sería texto.
 */
function documentoDeFacturacion(f: Facturacion): DocumentoExportable {
  const grupos = Object.keys(f.grupos).filter((g) => f.por_grupo[g] !== undefined)
  return {
    titulo: "Facturación por cobrar",
    subtitulo: subtituloDe(f),
    secciones: [
      {
        titulo: "Facturación por expediente",
        columnas: [
          { titulo: "Expediente", tipo: "texto" },
          { titulo: "Cliente", tipo: "texto" },
          { titulo: "Cobro", tipo: "texto" },
          { titulo: "Sub-estatus", tipo: "texto" },
          { titulo: "Moneda", tipo: "texto" },
          ...grupos.map((g): Columna => ({ titulo: f.grupos[g] ?? g, tipo: "monto" })),
          { titulo: "Pendiente", tipo: "monto" },
          { titulo: "Pendiente USD", tipo: "monto" },
        ],
        filas: f.expedientes.map((e) => ({
          celdas: [
            e.expediente,
            e.cliente,
            e.clase === "EXIGIBLE" ? "Esta semana" : "Semana próxima",
            e.subestatus,
            e.moneda,
            ...grupos.map((g) => (e.por_grupo[g] ? Number(e.por_grupo[g]) : null)),
            Number(e.pendiente),
            e.pendiente_usd === null ? null : Number(e.pendiente_usd),
          ],
        })),
      },
      {
        titulo: "Resumen en dólares",
        columnas: [
          { titulo: "Concepto", tipo: "texto" },
          { titulo: "USD", tipo: "monto" },
        ],
        filas: [
          { celdas: ["Esta semana (exigible)", Number(f.por_clase.EXIGIBLE ?? 0)] },
          { celdas: ["Semana próxima (en valija)", Number(f.por_clase.VALIJA ?? 0)] },
          ...grupos.map((g) => ({ celdas: [f.grupos[g] ?? g, Number(f.por_grupo[g])] })),
        ],
        pie: [["Total por cobrar", Number(f.total)]],
      },
    ],
  }
}
